import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import QRCredentialCard from "@/components/QRCredentialCard";
import MetricEntryForm from "@/components/MetricEntryForm";
import PlayerMetricsSection from "@/components/PlayerMetricsSection";
import AdminPlayerGameStats from "@/components/player/AdminPlayerGameStats";
import EvaluationForm from "@/components/EvaluationForm";
import EvaluationCard from "@/components/EvaluationCard";
import ShareButton from "@/components/ShareButton";
import CopyLinkButton from "@/components/CopyLinkButton";
import ImageUploadField from "@/components/ImageUploadField";
import TeamSelector from "@/components/TeamSelector";
import PlayerMediaManager from "@/components/PlayerMediaManager";
import SchoolSelect from "@/components/SchoolSelect";
import { Pencil, Save, X, Archive, RefreshCcw, Printer, Eye, EyeOff, Plus, Trash2, ShieldCheck, User, ClipboardList, GraduationCap, Download, Copy } from "lucide-react";

const PROVINCES = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];
const POSITIONS = ["RHP", "LHP", "C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "OF", "UTIL", "DH"];
const COMMITMENT_OPTIONS = ["uncommitted", "verbally_committed", "committed", "signed", "transferred"];
const COMMIT_STATUSES = ["uncommitted", "verbally_committed", "committed", "signed"];
const COMMIT_LEVELS = ["NCAA D1", "NCAA D2", "NCAA D3", "NAIA", "JUCO", "U Sports", "Pro"];

const VERIFICATION_BADGE = {
  cbr_verified: "bg-[#E63946] text-white",
  staff_entered: "bg-[#0A192F] text-white",
  self_reported: "bg-slate-200 text-slate-700",
};

const TABS = [
  { key: "overview", label: "Overview" },
  { key: "alumni", label: "Alumni" },
  { key: "media", label: "Media Library" },
  { key: "metrics", label: "Metrics" },
  { key: "gamestats", label: "Game Stats" },
  { key: "evaluations", label: "Evaluations" },
  { key: "registrations", label: "Camps & Registrations" },
  { key: "credential", label: "QR Credential" },
];

export default function AdminPlayerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [allCamps, setAllCamps] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [showEvalForm, setShowEvalForm] = useState(false);
  const [editingEval, setEditingEval] = useState(null);
  const [tab, setTab] = useState("overview");
  const [editing, setEditing] = useState(false);
  const [esub, setEsub] = useState("general");
  const [form, setForm] = useState({});
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [{ data: d }, { data: c }, { data: ev }] = await Promise.all([
      api.get(`/admin/players/${id}`),
      api.get("/camps", { params: { status_filter: "" } }),
      api.get("/evaluations", { params: { player_id: id } }),
    ]);
    setData(d);
    setAllCamps(c.items || []);
    setEvaluations(ev.items || []);
    setForm({
      first_name: d.player.first_name || "",
      last_name: d.player.last_name || "",
      graduation_year: d.player.graduation_year || "",
      primary_position: d.player.primary_position || "",
      secondary_position: d.player.secondary_position || "",
      bats: d.player.bats || "",
      throws: d.player.throws || "",
      height_cm: d.player.height_cm || "",
      weight_kg: d.player.weight_kg || "",
      gpa: d.player.gpa || "",
      school: d.player.school || "",
      current_team_id: d.player.current_team_id || "",
      current_team_name: d.player.current_team || "",
      date_of_birth: d.player.date_of_birth || "",
      show_age_publicly: d.player.show_age_publicly || false,
      city: d.player.city || "",
      province: d.player.province || "",
      country: d.player.country || "Canada",
      email: d.player.email || "",
      phone: d.player.phone || "",
      bio: d.player.bio || "",
      profile_photo_url: d.player.profile_photo_url || "",
      public_visible: d.player.public_visible !== false,
      commitment_status: d.player.recruiting?.commitment_status || "uncommitted",
      committed_to: d.player.recruiting?.committed_to || "",
      instagram_handle: d.player.instagram_handle || "",
      x_handle: d.player.x_handle || "",
      tiktok_handle: d.player.tiktok_handle || "",
      youtube_handle: d.player.youtube_handle || "",
      perfect_game_url: d.player.perfect_game_url || "",
      prep_baseball_url: d.player.prep_baseball_url || "",
      ncsa_url: d.player.ncsa_url || "",
      fieldlevel_url: d.player.fieldlevel_url || "",
      img_url: d.player.img_url || "",
      highlight_video_url: d.player.highlight_video_url || "",
      hudl_url: d.player.hudl_url || "",
      personal_website_url: d.player.personal_website_url || "",
      cbr_nation_alumni: d.player.cbr_nation_alumni || false,
      featured_alumni: d.player.featured_alumni || false,
      alumni_feature_order: d.player.alumni_feature_order ?? "",
      cbr_nation_team_year: d.player.cbr_nation_team_year || "",
      current_team_school: d.player.current_team_school || "",
      drafted_organization: d.player.drafted_organization || "",
      professional_organization: d.player.professional_organization || "",
      alumni_accomplishment: d.player.alumni_accomplishment || "",
      parent_name: d.player.parent_name || "",
      parent_cell: d.player.parent_cell || "",
      parent_email: d.player.parent_email || "",
    });
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const saveEdits = async () => {
    setError("");
    setBusy(true);
    try {
      const raw = { ...form };
      raw.graduation_year = raw.graduation_year ? Number(raw.graduation_year) : null;
      raw.height_cm = raw.height_cm ? Number(raw.height_cm) : null;
      raw.weight_kg = raw.weight_kg ? Number(raw.weight_kg) : null;
      raw.gpa = raw.gpa ? Number(raw.gpa) : null;
      raw.alumni_feature_order = raw.alumni_feature_order !== "" && raw.alumni_feature_order != null ? Number(raw.alumni_feature_order) : null;
      raw.recruiting = {
        commitment_status: raw.commitment_status || "uncommitted",
        committed_to: raw.committed_to || null,
      };
      delete raw.commitment_status;
      delete raw.committed_to;
      // Fields that may be intentionally cleared (send "" so backend clears them)
      const CLEARABLE = new Set(["secondary_position", "profile_photo_url"]);
      const payload = Object.fromEntries(Object.entries(raw).filter(([k, v]) => v !== undefined && (v !== "" || CLEARABLE.has(k))));
      await api.patch(`/players/${data.player.id}`, payload);
      setEditing(false);
      await load();
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const regenerateQR = async () => {
    if (!confirm("Regenerate this player's QR token? Existing printed cards will become invalid.")) return;
    await api.post(`/admin/players/${data.player.id}/regenerate-qr`);
    await load();
  };

  const toggleArchive = async () => {
    const next = !data.player.archived;
    await api.patch(`/admin/players/${data.player.id}/archive`, { archived: next });
    await load();
  };

  const toggleVisibility = async () => {
    await api.patch(`/players/${data.player.id}`, { public_visible: !data.player.public_visible });
    await load();
  };

  const assignCamp = async (campId) => {
    if (!campId) return;
    await api.post(`/admin/players/${data.player.id}/assign-camp`, {
      camp_id: campId,
      waive_payment: true,
    });
    await load();
  };

  const deleteMetric = async (entryId) => {
    if (!confirm("Delete this metric entry permanently?")) return;
    await api.delete(`/players/${data.player.id}/metrics/${entryId}`);
    await load();
  };

  const qrUrl = `${window.location.origin}/qr/${data?.player?.qr_code_token || ""}`;

  const downloadQr = async (fmt) => {
    try {
      const res = await api.get(`/admin/players/${data.player.id}/qr.${fmt}`, {
        params: { base_url: window.location.origin },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${String(data.registrations[0]?.player_number ?? 0).padStart(3, "0")}_${p.first_name}_${p.last_name}_${p.graduation_year || ""}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success(`QR ${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not download QR");
    }
  };

  const copyQrUrl = () => {
    navigator.clipboard?.writeText(qrUrl).then(() => toast.success("QR URL copied"), () => {});
  };

  if (!data) {
    return (
      <AdminLayout>
        <div className="p-8 label-overline">Loading player…</div>
      </AdminLayout>
    );
  }

  const p = data.player;

  return (
    <AdminLayout>
      <div className="p-4 sm:p-8 max-w-6xl w-full min-w-0 overflow-x-hidden">
        {/* Header strip */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-20 h-20 bg-slate-200 grid place-items-center overflow-hidden rounded-sm border border-slate-300 shrink-0">
              {p.profile_photo_url ? (
                <img src={p.profile_photo_url} alt={p.first_name} className="w-full h-full object-cover" />
              ) : (
                <span className="font-display text-2xl text-slate-500">{p.first_name?.[0]}{p.last_name?.[0]}</span>
              )}
            </div>
            <div className="min-w-0">
              <div className="label-overline text-[#E63946]">Internal Admin View</div>
              <h1 data-testid="admin-player-name" className="font-display text-3xl uppercase tracking-tighter mt-1">
                {p.first_name} {p.last_name}
              </h1>
              <div className="text-xs text-slate-600 mt-1 font-data">
                {p.graduation_year || "—"} · {p.primary_position || "—"} · {p.bats || "?"}/{p.throws || "?"} · {p.city || "—"}, {p.province || ""}
                {p.archived && <span className="ml-3 bg-slate-700 text-white px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm">Archived</span>}
                {!p.public_visible && <span className="ml-2 bg-slate-200 px-2 py-0.5 text-[10px] uppercase tracking-wider rounded-sm">Hidden</span>}
              </div>
              <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                <span data-testid="account-status-indicator" className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm ${p.user_id ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                  <User className="w-3 h-3" /> Account: {p.user_id ? "Claimed" : "Unclaimed"}
                </span>
                <span data-testid="alumni-status-indicator" className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-sm ${p.cbr_nation_alumni ? "bg-[#E63946]/10 text-[#E63946]" : "bg-slate-100 text-slate-500"}`}>
                  <GraduationCap className="w-3 h-3" /> CBR Nation Alumni: {p.cbr_nation_alumni ? "Yes" : "No"}
                </span>
                <button data-testid="edit-alumni-details-link" onClick={() => setTab("alumni")} className="text-[10px] font-semibold uppercase tracking-wider text-[#0A192F] hover:text-[#E63946] underline">Edit Alumni Details</button>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to={`/prospects/${p.slug}`} className="inline-flex items-center gap-1 px-3 py-2 border border-slate-300 rounded-sm text-xs font-semibold uppercase tracking-wider hover:bg-slate-50">
              <User className="w-3.5 h-3.5" /> Public View
            </Link>
            <CopyLinkButton
              url={`${window.location.origin}/prospects/${p.slug}`}
              label="Copy Profile Link"
              testid="admin-copy-profile-link"
              className="inline-flex items-center gap-1 px-3 py-2 border border-slate-300 rounded-sm text-xs font-semibold uppercase tracking-wider hover:bg-slate-50 text-[#0A192F]"
            />
            <button data-testid="admin-toggle-visibility" onClick={toggleVisibility} className="inline-flex items-center gap-1 px-3 py-2 border border-slate-300 rounded-sm text-xs font-semibold uppercase tracking-wider hover:bg-slate-50">
              {p.public_visible ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              {p.public_visible ? "Public" : "Hidden"}
            </button>
            <button data-testid="admin-toggle-archive" onClick={toggleArchive} className="inline-flex items-center gap-1 px-3 py-2 border border-slate-300 rounded-sm text-xs font-semibold uppercase tracking-wider hover:bg-slate-50">
              <Archive className="w-3.5 h-3.5" /> {p.archived ? "Restore" : "Archive"}
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-8 border-b border-slate-200 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider border-b-2 ${
                tab === t.key ? "border-[#E63946] text-[#0A192F]" : "border-transparent text-slate-500 hover:text-slate-800"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Overview tab */}
        {tab === "overview" && (
          <div className="mt-6 grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white border border-slate-200 p-6 rounded-sm">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-lg uppercase tracking-tight">Profile</h3>
                  {!editing ? (
                    <button data-testid="admin-edit-button" onClick={() => setEditing(true)} className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#E63946]">
                      <Pencil className="w-3.5 h-3.5" /> Edit
                    </button>
                  ) : (
                    <div className="flex gap-2">
                      <button onClick={() => { setEditing(false); load(); }} className="inline-flex items-center gap-1 text-xs uppercase tracking-wider"><X className="w-3.5 h-3.5" /> Cancel</button>
                      <button data-testid="admin-save-button" onClick={saveEdits} disabled={busy} className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-[#E63946] disabled:opacity-50"><Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save"}</button>
                    </div>
                  )}
                </div>
                {error && <div className="border-l-4 border-[#E63946] bg-red-50 p-2 text-xs text-[#E63946] mt-3">{error}</div>}

                {!editing ? (
                  <div className="grid sm:grid-cols-2 gap-4 mt-4 text-sm">
                    {[
                      ["School", p.school],
                      ["Current Team", p.current_team],
                      ["Height", p.height_cm ? `${p.height_cm} cm` : null],
                      ["Weight", p.weight_kg ? `${p.weight_kg} kg` : null],
                      ["GPA", p.gpa],
                      ["Email", p.email],
                      ["Phone", p.phone],
                      ["Commitment", p.recruiting?.commitment_status],
                      ["Committed To", p.recruiting?.committed_to],
                    ].map(([k, v]) => (
                      <div key={k}>
                        <div className="label-overline">{k}</div>
                        <div className="mt-1">{v || <span className="text-slate-400">—</span>}</div>
                      </div>
                    ))}
                    <div className="sm:col-span-2">
                      <div className="label-overline">Bio</div>
                      <div className="mt-1 text-slate-700 whitespace-pre-line">{p.bio || <span className="text-slate-400">—</span>}</div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-4">
                    <div className="flex gap-1 border-b border-slate-200 mb-4 overflow-x-auto">
                      {[["general", "General"], ["photo", "Photo"], ["recruiting", "Recruiting"], ["social", "Social"]].map(([k, l]) => (
                        <button key={k} type="button" data-testid={`edit-subtab-${k}`} onClick={() => setEsub(k)}
                          className={`px-3 py-2 text-[11px] font-semibold uppercase tracking-wider border-b-2 ${esub === k ? "border-[#E63946] text-[#0A192F]" : "border-transparent text-slate-400 hover:text-slate-700"}`}>{l}</button>
                      ))}
                    </div>

                    {esub === "general" && (
                      <div className="grid sm:grid-cols-2 gap-3">
                        {[
                          ["first_name", "First Name"], ["last_name", "Last Name"],
                          ["graduation_year", "Grad Year (number)"],
                          ["height_cm", "Height (cm)"], ["weight_kg", "Weight (kg)"], ["gpa", "GPA"],
                          ["school", "School"],
                          ["city", "City"],
                          ["email", "Email"], ["phone", "Phone"],
                          ["committed_to", "Committed To"],
                        ].map(([key, label]) => (
                          <div key={key}>
                            <label className="label-overline">{label}</label>
                            <input data-testid={`admin-edit-${key}`} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                          </div>
                        ))}
                        <div>
                          <label className="label-overline">Primary Position</label>
                          <select value={form.primary_position} onChange={(e) => setForm({ ...form, primary_position: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
                            <option value="">—</option>
                            {POSITIONS.map((pp) => <option key={pp} value={pp}>{pp}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="label-overline">Secondary Position</label>
                          <select data-testid="admin-edit-secondary_position" value={form.secondary_position} onChange={(e) => setForm({ ...form, secondary_position: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
                            <option value="">— None —</option>
                            {POSITIONS.map((pp) => <option key={pp} value={pp}>{pp}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="label-overline">Province</label>
                          <select value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
                            <option value="">—</option>
                            {PROVINCES.map((pp) => <option key={pp} value={pp}>{pp}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="label-overline">Bats</label>
                          <select value={form.bats} onChange={(e) => setForm({ ...form, bats: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
                            <option value="">—</option><option value="L">Left</option><option value="R">Right</option><option value="S">Switch</option>
                          </select>
                        </div>
                        <div>
                          <label className="label-overline">Throws</label>
                          <select value={form.throws} onChange={(e) => setForm({ ...form, throws: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
                            <option value="">—</option><option value="L">Left</option><option value="R">Right</option>
                          </select>
                        </div>
                        <div>
                          <label className="label-overline">Commitment Status</label>
                          <select value={form.commitment_status} onChange={(e) => setForm({ ...form, commitment_status: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
                            {COMMITMENT_OPTIONS.map((cc) => <option key={cc} value={cc}>{cc.replace("_", " ")}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="label-overline">Date of Birth <span className="normal-case text-slate-400">(private)</span></label>
                          <input data-testid="admin-edit-date_of_birth" type="date" value={form.date_of_birth || ""} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="label-overline">Current Team</label>
                          <div className="mt-1"><TeamSelector value={form.current_team_id} initialTeam={form.current_team_id ? { id: form.current_team_id, name: form.current_team_name } : null} onChange={(t) => setForm({ ...form, current_team_id: t?.id || "", current_team_name: t?.name || "" })} testidPrefix="admin-edit-team" /></div>
                        </div>
                        <label className="sm:col-span-2 flex items-center gap-2 text-xs text-slate-600">
                          <input data-testid="admin-edit-show_age" type="checkbox" checked={!!form.show_age_publicly} onChange={(e) => setForm({ ...form, show_age_publicly: e.target.checked })} />
                          Show age (not birthdate) on public profile. Full DOB is never public.
                        </label>
                        <div className="sm:col-span-2 border-t border-slate-100 pt-3 mt-1">
                          <div className="label-overline text-[#E63946]">Parent / Guardian Contact <span className="normal-case text-slate-400">(private — never shown publicly)</span></div>
                          <p className="text-[11px] text-slate-400 mt-1">Used to prefill registrations and as the primary recipient for WWBA invites.</p>
                          <div className="grid sm:grid-cols-3 gap-3 mt-2">
                            <div><label className="label-overline">Parent Name</label><input data-testid="admin-edit-parent_name" value={form.parent_name || ""} onChange={(e) => setForm({ ...form, parent_name: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></div>
                            <div><label className="label-overline">Parent Cell</label><input data-testid="admin-edit-parent_cell" value={form.parent_cell || ""} onChange={(e) => setForm({ ...form, parent_cell: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></div>
                            <div><label className="label-overline">Parent Email</label><input data-testid="admin-edit-parent_email" type="email" value={form.parent_email || ""} onChange={(e) => setForm({ ...form, parent_email: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" /></div>
                          </div>
                        </div>
                        <div className="sm:col-span-2">
                          <label className="label-overline">Bio</label>
                          <textarea rows="3" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                        </div>
                      </div>
                    )}

                    {esub === "photo" && (
                      <ImageUploadField label="Profile Photo" testid="admin-edit-profile-photo" value={form.profile_photo_url} onChange={(url) => setForm({ ...form, profile_photo_url: url })} />
                    )}

                    {esub === "recruiting" && (
                      <div className="grid sm:grid-cols-2 gap-3">
                        <p className="sm:col-span-2 text-[11px] text-slate-400">Full URLs for recruiting services. Shown publicly under "Connect &amp; Recruiting Profiles".</p>
                        {[
                          ["committed_to", "Committed To"],
                          ["perfect_game_url", "Perfect Game URL"], ["prep_baseball_url", "Prep Baseball Report URL"],
                          ["ncsa_url", "NCSA URL"], ["fieldlevel_url", "FieldLevel URL"],
                          ["img_url", "IMG Academy URL"], ["highlight_video_url", "Highlight Video URL"],
                          ["hudl_url", "Hudl URL"], ["personal_website_url", "Personal Website URL"],
                        ].map(([key, label]) => (
                          <div key={key}>
                            <label className="label-overline">{label}</label>
                            <input data-testid={`admin-edit-${key}`} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                          </div>
                        ))}
                      </div>
                    )}

                    {esub === "social" && (
                      <div className="grid sm:grid-cols-2 gap-3">
                        <p className="sm:col-span-2 text-[11px] text-slate-400">Handles without @ (Instagram/X/TikTok/YouTube).</p>
                        {[
                          ["instagram_handle", "Instagram handle"], ["x_handle", "X / Twitter handle"],
                          ["tiktok_handle", "TikTok handle"], ["youtube_handle", "YouTube handle"],
                        ].map(([key, label]) => (
                          <div key={key}>
                            <label className="label-overline">{label}</label>
                            <input data-testid={`admin-edit-${key}`} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Alumni fields moved to their own top-level "Alumni" tab for discoverability */}
                  </div>
                )}
              </div>

              <PlayerMetricsSection player={p} />
            </div>

            {/* Sidebar */}
            <aside className="space-y-4">
              <div className="bg-white border border-slate-200 p-5 rounded-sm">
                <div className="label-overline">Parents / Guardians</div>
                {(data.parents || []).length === 0 ? (
                  <p className="text-xs text-slate-500 mt-2">No parent accounts linked.</p>
                ) : (
                  <ul className="mt-2 space-y-1 text-sm">
                    {data.parents.map((u) => (
                      <li key={u.id} className="flex justify-between"><span>{u.name}</span><span className="text-xs text-slate-500">{u.email}</span></li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="bg-white border border-slate-200 p-5 rounded-sm">
                <div className="label-overline">CBR Overall Rating</div>
                <div className="font-data text-5xl mt-1 text-[#0A192F]">{p.cbr_overall_rating ?? "—"}</div>
                {p.rating_components && (
                  <div className="text-[11px] text-slate-500 mt-2 space-y-0.5">
                    <div>Metric score: <span className="font-data">{p.rating_components.metric_score?.toFixed?.(1) ?? "—"}</span></div>
                    <div>Eval score: <span className="font-data">{p.rating_components.evaluation_score?.toFixed?.(1) ?? "—"}</span> ({p.rating_components.evaluation_count} evals)</div>
                  </div>
                )}
                <button
                  data-testid="recompute-rating-button"
                  onClick={async () => { await api.post(`/admin/players/${p.id}/recompute-rating`); load(); }}
                  className="mt-3 text-xs font-semibold uppercase tracking-wider text-[#E63946]"
                >
                  Recompute →
                </button>
              </div>

              <CommitmentCard player={p} onSaved={load} />
              <PlayerAccountCard player={p} />
            </aside>
          </div>
        )}

        {/* Alumni tab */}
        {tab === "alumni" && (
          <div className="mt-6 max-w-3xl" data-testid="admin-alumni-tab">
            <div className="bg-white border border-slate-200 p-6 rounded-sm">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-display text-lg uppercase tracking-tight flex items-center gap-2"><GraduationCap className="w-5 h-5 text-[#E63946]" /> CBR Nation Alumni</h3>
                  <p className="text-[11px] text-slate-500 mt-1">Admin-only. Turning this on adds the player to the public CBR Nation Alumni page instantly; turning it off removes them without deleting any history.</p>
                </div>
                <button data-testid="alumni-save-button" onClick={saveEdits} disabled={busy} className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#E63946] disabled:opacity-50 shrink-0"><Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save Alumni"}</button>
              </div>
              {error && <div className="border-l-4 border-[#E63946] bg-red-50 p-2 text-xs text-[#E63946] mt-3">{error}</div>}
              <div className="grid sm:grid-cols-2 gap-3 mt-4" data-testid="alumni-edit-section">
                <div className="sm:col-span-2 flex items-center justify-between bg-slate-50 border border-slate-200 rounded-sm px-4 py-3">
                  <div>
                    <div className="font-semibold text-sm">CBR Nation Alumni Status</div>
                    <div className="text-[11px] text-slate-500">Appears on <Link to="/cbr-nation-alumni" className="underline hover:text-[#E63946]">/cbr-nation-alumni</Link> when on.</div>
                  </div>
                  <label className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider">
                    <input data-testid="alumni-toggle" type="checkbox" checked={!!form.cbr_nation_alumni} onChange={(e) => setForm({ ...form, cbr_nation_alumni: e.target.checked })} className="w-4 h-4" />
                    {form.cbr_nation_alumni ? "On" : "Off"}
                  </label>
                </div>
                <div className="sm:col-span-2 flex items-center gap-6">
                  <label className="flex items-center gap-2 text-xs"><input data-testid="alumni-featured" type="checkbox" checked={!!form.featured_alumni} onChange={(e) => setForm({ ...form, featured_alumni: e.target.checked })} /> Featured Alumni</label>
                  <div className="flex items-center gap-2 text-xs"><span className="label-overline">Feature Order</span><input data-testid="alumni-order" type="number" value={form.alumni_feature_order} onChange={(e) => setForm({ ...form, alumni_feature_order: e.target.value })} className="w-20 px-2 py-1 border border-slate-300 rounded-sm" /></div>
                </div>
                {[
                  ["cbr_nation_team_year", "CBR Nation Team / Year"],
                  ["current_team_school", "Current Team or School"],
                  ["drafted_organization", "Drafted Organization"],
                  ["professional_organization", "Professional Organization"],
                ].map(([key, label]) => (
                  <div key={key}>
                    <label className="label-overline">{label}</label>
                    <input data-testid={`admin-edit-${key}`} value={form[key] || ""} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                  </div>
                ))}
                <div className="sm:col-span-2">
                  <label className="label-overline">Alumni Accomplishment</label>
                  <textarea data-testid="admin-edit-alumni_accomplishment" value={form.alumni_accomplishment || ""} onChange={(e) => setForm({ ...form, alumni_accomplishment: e.target.value })} rows={2} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
                </div>
                <p className="sm:col-span-2 text-[11px] text-slate-400">Reuses the player's photo, position, grad year, commitment, school &amp; awards on the public Alumni page.</p>
              </div>
            </div>
          </div>
        )}

        {/* Media Library tab */}
        {tab === "media" && (
          <div className="mt-6 max-w-4xl" data-testid="admin-media-tab">
            <p className="text-xs text-slate-500 mb-3">Upload photos & videos or add YouTube / Vimeo / Hudl / FieldLevel links. Approve player-submitted media and set the Featured Video here.</p>
            <PlayerMediaManager playerId={p.id} mode="admin" />
          </div>
        )}

        {/* Metrics tab */}
        {tab === "metrics" && (
          <div className="mt-6 grid lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3">
              <MetricEntryForm playerId={p.id} camps={allCamps} onSaved={load} />
            </div>

            <div className="lg:col-span-2 bg-white border border-slate-200 rounded-sm p-5">
              <div className="label-overline">Latest Verified</div>
              <div className="space-y-2 mt-3">
                {Object.entries(p.verified_metrics || {}).length === 0 && (
                  <p className="text-xs text-slate-500">No metrics yet. Use the form to record the first measurement.</p>
                )}
                {Object.entries(p.verified_metrics || {}).map(([k, m]) => (
                  <div key={k} className="flex items-center justify-between border-b border-slate-100 pb-2">
                    <div>
                      <div className="text-xs uppercase tracking-wider text-slate-500">{k.replace(/_/g, " ")}</div>
                      <div className="font-data text-lg text-[#0A192F]">{m.value} <span className="text-xs text-slate-500">{m.units}</span></div>
                    </div>
                    <span className={`text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 ${VERIFICATION_BADGE[m.verification_status]}`}>
                      {m.verification_status.replace("_", " ")}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Full history table */}
            <div className="lg:col-span-5 bg-white border border-slate-200 rounded-sm overflow-x-auto">
              <div className="p-4 border-b border-slate-200">
                <h3 className="font-display text-lg uppercase tracking-tight">All Recorded Measurements ({data.metric_entries.length})</h3>
              </div>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left p-3 label-overline">Metric</th>
                    <th className="text-left p-3 label-overline">Value</th>
                    <th className="text-left p-3 label-overline">Event</th>
                    <th className="text-left p-3 label-overline">Date</th>
                    <th className="text-left p-3 label-overline">Status</th>
                    <th className="text-left p-3 label-overline">Verified By</th>
                    <th className="text-right p-3 label-overline">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {data.metric_entries.length === 0 ? (
                    <tr><td colSpan="7" className="p-6 text-center text-sm text-slate-500">No metrics recorded yet.</td></tr>
                  ) : (
                    data.metric_entries.map((m) => (
                      <tr key={m.id} className="border-b border-slate-100 last:border-0">
                        <td className="p-3">{m.metric_type.replace(/_/g, " ")}</td>
                        <td className="p-3 font-data">{m.value} {m.units}</td>
                        <td className="p-3">{m.event_name || "—"}</td>
                        <td className="p-3 text-xs">{m.event_date ? new Date(m.event_date).toLocaleDateString("en-CA") : "—"}</td>
                        <td className="p-3"><span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${VERIFICATION_BADGE[m.verification_status]}`}>{m.verification_status.replace("_", " ")}</span></td>
                        <td className="p-3 text-xs">{m.verified_by_name || "—"}</td>
                        <td className="p-3 text-right">
                          <button data-testid={`delete-metric-${m.id}`} onClick={() => deleteMetric(m.id)} className="text-[#E63946]">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Game Stats tab */}
        {tab === "gamestats" && (
          <AdminPlayerGameStats playerId={id} primaryPosition={data.player.primary_position} />
        )}

        {/* Evaluations tab */}
        {tab === "evaluations" && (
          <div className="mt-6 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl uppercase tracking-tight">
                <ClipboardList className="inline w-5 h-5 mr-2 text-[#E63946]" />
                Scouting Evaluations ({evaluations.length})
              </h3>
              <button
                data-testid="admin-new-evaluation"
                onClick={() => setShowEvalForm(!showEvalForm)}
                className="bg-[#0A192F] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"
              >
                {showEvalForm ? "Cancel" : "+ New Evaluation"}
              </button>
            </div>

            {showEvalForm && (
              <EvaluationForm
                playerId={p.id}
                playerPosition={p.primary_position}
                camps={allCamps}
                onSaved={async () => { setShowEvalForm(false); await load(); }}
              />
            )}

            {editingEval && (
              <div data-testid="admin-edit-evaluation" className="border-2 border-[#E63946]/40 rounded-sm p-1">
                <div className="flex items-center justify-between px-3 py-2">
                  <span className="label-overline text-[#E63946]">Editing evaluation</span>
                  <button onClick={() => setEditingEval(null)} className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 hover:text-[#E63946]">Cancel</button>
                </div>
                <EvaluationForm
                  playerId={p.id}
                  playerPosition={p.primary_position}
                  camps={allCamps}
                  initial={editingEval}
                  onSaved={async () => { setEditingEval(null); await load(); toast.success("Evaluation updated"); }}
                />
              </div>
            )}

            <div className="grid lg:grid-cols-2 gap-4">
              {evaluations.length === 0 ? (
                <div className="lg:col-span-2 bg-white border border-dashed border-slate-300 p-8 text-center text-sm text-slate-500">
                  No evaluations recorded yet. Click &quot;New Evaluation&quot; to submit a 20-80 report.
                </div>
              ) : (
                evaluations.map((e) => (
                  <EvaluationCard
                    key={e.id}
                    evaluation={e}
                    onEdit={(ev) => { setShowEvalForm(false); setEditingEval(ev); }}
                    onDelete={async (ev) => {
                      if (!window.confirm("Delete this evaluation? This updates the player's CBR rating.")) return;
                      try {
                        await api.delete(`/evaluations/${ev.id}`);
                        toast.success("Evaluation deleted");
                        if (editingEval?.id === ev.id) setEditingEval(null);
                        await load();
                      } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
                    }}
                  />
                ))
              )}
            </div>
          </div>
        )}

        {/* Registrations tab */}
        {tab === "registrations" && (
          <div className="mt-6 space-y-5">
            <div className="bg-white border border-slate-200 rounded-sm p-5">
              <div className="flex items-center justify-between">
                <h3 className="font-display text-lg uppercase tracking-tight">Assign to Camp</h3>
                <ShieldCheck className="w-5 h-5 text-[#E63946]" />
              </div>
              <p className="text-xs text-slate-500 mt-1">Admin assignment creates a confirmed registration with $0 amount (comp / invited).</p>
              <select
                data-testid="admin-assign-camp-select"
                onChange={(e) => assignCamp(e.target.value)}
                className="mt-4 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm"
                value=""
              >
                <option value="">— Select camp to assign —</option>
                {allCamps.filter((c) => c.status !== "draft").map((c) => (
                  <option key={c.id} value={c.id}>{c.title} · {new Date(c.start_date).toLocaleDateString("en-CA")}</option>
                ))}
              </select>
            </div>

            <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left p-3 label-overline">Camp</th>
                    <th className="text-left p-3 label-overline">Date</th>
                    <th className="text-left p-3 label-overline">Player #</th>
                    <th className="text-left p-3 label-overline">Status</th>
                    <th className="text-left p-3 label-overline">Attendance</th>
                    <th className="text-left p-3 label-overline">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.registrations.length === 0 ? (
                    <tr><td colSpan="6" className="p-6 text-center text-slate-500">No registrations.</td></tr>
                  ) : data.registrations.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100 last:border-0">
                      <td className="p-3 font-semibold">{r.camp?.title || "—"}</td>
                      <td className="p-3 text-xs">{r.camp ? new Date(r.camp.start_date).toLocaleDateString("en-CA") : "—"}</td>
                      <td className="p-3">
                        <input
                          data-testid={`player-number-input-${r.id}`}
                          type="number"
                          defaultValue={r.player_number ?? ""}
                          min="0"
                          max="999"
                          onBlur={async (e) => {
                            const v = Number(e.target.value);
                            if (!isNaN(v) && v !== r.player_number) {
                              try {
                                await api.patch(`/admin/registrations/${r.id}/player-number`, { player_number: v });
                                await load();
                                toast.success("Jersey number updated");
                              } catch (err) {
                                toast.error(formatApiError(err?.response?.data?.detail) || "Could not update player number");
                              }
                            }
                          }}
                          className="w-16 px-2 py-1 border border-slate-300 rounded-sm font-data text-center"
                        />
                      </td>
                      <td className="p-3">{r.status}</td>
                      <td className="p-3">{r.attendance_status}</td>
                      <td className="p-3 font-data">${r.amount_due || 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Credential tab */}
        {tab === "credential" && (
          <div className="mt-6 grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <div className="bg-white border border-slate-200 rounded-sm p-5 mb-4">
                <h3 className="font-display text-lg uppercase tracking-tight">QR Credential</h3>
                <p className="text-xs text-slate-500 mt-1">Printable player card with embedded QR. Scanning opens admin or public profile depending on viewer.</p>
                <div className="mt-4 flex gap-2 flex-wrap">
                  <button data-testid="print-credential" onClick={() => window.print()} className="inline-flex items-center gap-1 bg-[#0A192F] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm">
                    <Printer className="w-4 h-4" /> Print
                  </button>
                  <button data-testid="download-qr-png" onClick={() => downloadQr("png")} className="inline-flex items-center gap-1 bg-[#E63946] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm">
                    <Download className="w-4 h-4" /> QR PNG
                  </button>
                  <button data-testid="download-qr-svg" onClick={() => downloadQr("svg")} className="inline-flex items-center gap-1 bg-[#E63946] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm">
                    <Download className="w-4 h-4" /> QR SVG
                  </button>
                  <button data-testid="copy-qr-url" onClick={copyQrUrl} className="inline-flex items-center gap-1 border border-slate-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm">
                    <Copy className="w-4 h-4" /> Copy QR URL
                  </button>
                  <button data-testid="regenerate-qr" onClick={regenerateQR} className="inline-flex items-center gap-1 border border-slate-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm">
                    <RefreshCcw className="w-4 h-4" /> Regenerate Token
                  </button>
                  <a
                    href={qrUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 border border-slate-300 px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm"
                  >
                    Test Scan Link
                  </a>
                  <ShareButton
                    url={`${window.location.origin}/prospects/${p.slug}`}
                    title={`${p.first_name} ${p.last_name} · CBR Prospects`}
                  />
                </div>
                <div className="mt-4 text-xs text-slate-600">
                  <div>QR URL:</div>
                  <div className="mt-1 font-mono text-[11px] break-all bg-slate-50 p-2">{qrUrl}</div>
                </div>
              </div>
            </div>

            <div className="flex justify-center print:justify-start">
              <QRCredentialCard player={p} qrUrl={qrUrl} campTitle={data.registrations[0]?.camp?.title} playerNumber={data.registrations[0]?.player_number} />
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

function CommitmentCard({ player, onSaved }) {
  const c = player.commitment || {};
  const toSchool = (cc) => {
    if (cc.school_id) return { id: cc.school_id, name: cc.school_name, slug: cc.school_slug, logo_url: cc.school_logo_url, logo_dark_url: cc.school_logo_dark_url, division: cc.school_division, association: cc.school_association };
    if (cc.school_name) return { id: null, name: cc.school_name };
    return null;
  };
  const [school, setSchool] = useState(toSchool(c));
  const [form, setForm] = useState({
    commitment_status: c.commitment_status || "uncommitted",
    level: c.level || "",
    commitment_date: c.commitment_date || "",
    sport: c.sport || "",
    scholarship_type: c.scholarship_type || "",
    notes: c.notes || "",
    public_visible: !!c.public_visible,
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const cc = player.commitment || {};
    setSchool(toSchool(cc));
    setForm({
      commitment_status: cc.commitment_status || "uncommitted",
      level: cc.level || "",
      commitment_date: cc.commitment_date || "",
      sport: cc.sport || "",
      scholarship_type: cc.scholarship_type || "",
      notes: cc.notes || "",
      public_visible: !!cc.public_visible,
    });
  }, [player.commitment]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        level: form.level || null,
        commitment_date: form.commitment_date || null,
        sport: form.sport || null,
        scholarship_type: form.scholarship_type || null,
        notes: form.notes || null,
        school_id: school?.id || null,
        school_name: school?.name || null,
      };
      const { data } = await api.put(`/admin/players/${player.id}/commitment`, payload);
      toast.success(data.media_draft_created ? "Commitment saved · media draft queued" : "Commitment saved");
      onSaved && onSaved();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const regenerate = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/players/${player.id}/commitment/regenerate-draft`);
      toast.success("New commitment draft sent to the Media Engine queue");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Could not regenerate (check media consent)"); }
    finally { setBusy(false); }
  };

  const hasPublicCommitment = !!(c.public_visible && c.school_name);

  return (
    <div data-testid="commitment-card" className="bg-white border border-slate-200 p-5 rounded-sm">
      <div className="flex items-center gap-2 label-overline"><GraduationCap className="w-4 h-4 text-[#E63946]" /> College Commitment</div>
      <p className="text-[10px] text-slate-400 mt-1">Admin-entered only. Select a school from the directory (logo reused everywhere).</p>
      <div className="space-y-3 mt-3">
        <div>
          <label className="label-overline text-[10px]">Status</label>
          <select data-testid="commitment-status" value={form.commitment_status} onChange={(e) => setForm({ ...form, commitment_status: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
            {COMMIT_STATUSES.map((o) => <option key={o} value={o}>{o.replace(/_/g, " ")}</option>)}
          </select>
        </div>
        <div>
          <label className="label-overline text-[10px]">School</label>
          <SchoolSelect value={school} onChange={setSchool} testid="commitment-school-select" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label-overline text-[10px]">Level</label>
            <select data-testid="commitment-level" value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm bg-white text-sm">
              <option value="">—</option>
              {COMMIT_LEVELS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className="label-overline text-[10px]">Sport / Program</label>
            <input data-testid="commitment-sport" value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} placeholder="Baseball" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label-overline text-[10px]">Commitment Date</label>
            <input data-testid="commitment-date" type="date" value={form.commitment_date} onChange={(e) => setForm({ ...form, commitment_date: e.target.value })} className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </div>
          <div>
            <label className="label-overline text-[10px]">Scholarship</label>
            <input data-testid="commitment-scholarship" value={form.scholarship_type} onChange={(e) => setForm({ ...form, scholarship_type: e.target.value })} placeholder="e.g. Partial" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          </div>
        </div>
        <div>
          <label className="label-overline text-[10px]">Internal Notes (admin-only)</label>
          <textarea data-testid="commitment-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} placeholder="Not shown publicly" className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input data-testid="commitment-public" type="checkbox" checked={form.public_visible} onChange={(e) => setForm({ ...form, public_visible: e.target.checked })} />
          <span>Show on public profile</span>
        </label>
        <button data-testid="commitment-save" onClick={save} disabled={busy} className="w-full bg-[#0A192F] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60">
          {busy ? "Saving…" : "Save Commitment"}
        </button>
        {hasPublicCommitment && (
          <button data-testid="commitment-regenerate-draft" onClick={regenerate} disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 border border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60 transition-colors">
            <RefreshCcw className="w-3.5 h-3.5" /> Regenerate Commitment Graphic
          </button>
        )}
      </div>
    </div>
  );
}


function PlayerAccountCard({ player }) {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState(player.email || "");

  const load = useCallback(() => {
    api.get(`/admin/players/${player.id}/account`).then(({ data }) => setStatus(data)).catch(() => setStatus(null));
  }, [player.id]);
  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!email.trim()) { toast.error("Enter the player's email to send an invite"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/players/${player.id}/invite`, { email: email.trim() });
      toast.success(`Invite email sent to ${data.email}`);
      navigator.clipboard?.writeText(data.claim_link).catch(() => {});
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const revoke = async () => {
    if (!window.confirm("Revoke this pending invite? The existing claim link will stop working.")) return;
    setBusy(true);
    try { await api.post(`/admin/players/${player.id}/revoke-invite`); toast.success("Invite revoked"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const release = async () => {
    if (!window.confirm("Release this claimed account? It will be unlinked (all player data is preserved) so you can re-invite with the correct email. The player will need to claim again.")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/players/${player.id}/release-account`);
      toast.success(data.freed_email ? `Account released — ${data.freed_email} freed` : "Account released");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const copyLink = (link) => { navigator.clipboard?.writeText(link); toast.success("Claim link copied"); };
  const btn = "inline-flex items-center gap-1.5 border border-slate-300 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider rounded-sm hover:bg-slate-50 disabled:opacity-50";

  return (
    <div data-testid="player-account-card" className="bg-white border border-slate-200 p-5 rounded-sm">
      <div className="flex items-center gap-2 label-overline"><User className="w-4 h-4 text-[#E63946]" /> Player Account</div>
      {player.complete_recruiting_profile && (
        <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-1 rounded-sm">Complete Recruiting Profile</div>
      )}
      {status === null ? (
        <p className="text-xs text-slate-400 mt-2">Loading…</p>
      ) : status.linked ? (
        <div className="mt-2 text-sm" data-testid="account-status-claimed">
          <div className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-1 rounded-sm">Claimed</div>
          <p className="text-xs text-slate-500 mt-2">{status.user?.name} · {status.user?.email}</p>
          {status.user?.last_login_at && <p className="text-[10px] text-slate-400 mt-1">Last login {new Date(status.user.last_login_at).toLocaleDateString("en-CA")}</p>}
          <button data-testid="release-account-btn" onClick={release} disabled={busy} className={`${btn} mt-3 text-[#E63946] border-[#E63946]/40`}>Release Account</button>
          <p className="text-[10px] text-slate-400 mt-2">Wrong email or need to re-assign? Releasing unlinks this account (all player data is kept) so you can re-invite with the correct email.</p>
        </div>
      ) : status.pending_invite ? (
        <div className="mt-2" data-testid="account-status-invited">
          <div className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-1 rounded-sm">Invite Sent</div>
          <p className="text-xs text-slate-500 mt-2 break-all">{status.pending_invite.email}</p>
          <div className="flex flex-wrap gap-2 mt-3">
            <button data-testid="copy-invite-link" onClick={() => copyLink(status.pending_invite.claim_link)} className={btn}><Copy className="w-3.5 h-3.5" /> Copy Link</button>
            <button data-testid="resend-player-btn" onClick={invite} disabled={busy} className={btn}>Resend</button>
            <button data-testid="revoke-invite-btn" onClick={revoke} disabled={busy} className={`${btn} text-[#E63946] border-[#E63946]/40`}>Revoke</button>
          </div>
          <p className="text-[10px] text-slate-400 mt-2">Invite email sent — copy the link as a backup. Expires {new Date(status.pending_invite.expires_at).toLocaleDateString("en-CA")}.</p>
        </div>
      ) : (
        <div className="mt-2" data-testid="account-status-unclaimed">
          <div className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-slate-100 text-slate-500 px-2 py-1 rounded-sm">Unclaimed</div>
          <p className="text-xs text-slate-500 mt-2">Invite this player to claim & manage their own profile. Their metrics, evaluations, rankings, media and history stay intact.</p>
          <input data-testid="invite-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="player@email.com" className="mt-3 w-full px-3 py-2 border border-slate-300 rounded-sm text-sm" />
          <button data-testid="invite-player-btn" onClick={invite} disabled={busy} className="mt-2 w-full bg-[#0A192F] text-white px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60">
            {busy ? "Sending…" : "Invite Player to Claim Profile"}
          </button>
        </div>
      )}
    </div>
  );
}
